import { FormLabel } from "../../components/form-label/FormLabel";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint, permissions } from "../../services/shared";
import { PermissionItem } from "../../types";
import React, { useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import {
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { AxiosError } from "axios";
import { useAuth } from "../../providers/AuthProvider";
import { apiClient } from "../../services/api";

type FormData = z.infer<typeof formSchema>;
const formSchema = z.object({
  permission: z.object({
    label: z.string(),
    grant: z.string(),
  }),
});

const options: PermissionItem[] = permissions;
export default function AutorizzazioniUtente() {
  const { user, refreshUser, customClaims } = useAuth();
  const [snackState, setSnackState] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({
    severity: "success",
    message: "",
  });
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      permission: { label: "", grant: "" },
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;
  const updateData = async (
    data: { uid: string; grants?: string[]; grant?: string },
    method: "update" | "remove-grant",
  ): Promise<void> => {
    let res = null;
    let payload: Record<string, any> = {};
    if (method === "remove-grant") {
        payload.uid = data.uid;
        payload.grant = data.grant
    } else {
        payload.uid = data.uid;
        payload.grants = data.grants
    }
    try {
      res = await apiClient.post(
        `/v1/users/${method}/${payload.uid}`,
        payload,
        {
          headers: {
            "Content-Type": `application/json`,
          },
        },
      );
      await refreshUser();
    } catch (e: AxiosError | any) {
      console.error("errore", e);
      setSnackMessage({
        severity: "error",
        message: e?.response.data.error,
      });
      setSnackState(true);
    }
    if (res?.status && res?.status === 200) {
      setSnackMessage({ severity: "success", message: res?.data.message });
      setSnackState(true);
      await refreshUser();
      reset();
    }
  };
  const handleDelete = async (data: string) => {
    setSnackState(false);
    const payload = {
      uid: user?.uid as string,
      grant: data,
    };
    await updateData(payload, "remove-grant");
  };
  const onSubmit = handleSubmit(async (data) => {
    setSnackState(false);
    if (!user || !user.uid) {
      setSnackMessage({
        severity: "error",
        message: "User non è autenticato",
      });
      setSnackState(true);
      return;
    }
    const payload = {
      uid: user.uid,
      grants: [data?.permission.grant],
    };
    console.log({ payload });
    await updateData(payload, "update");
  });
  return (
    <div style={{ width: "100%", paddingBottom: "1em" }}>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackMessage.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
      <FormLabel label={"Autorizzazioni utente"}></FormLabel>
      <form
        onSubmit={onSubmit}
        style={{
          padding: "0 1em",
          margin: "auto",
          marginBottom: "1em",
          maxWidth: "100%",
          background: "white",
        }}
        className="flex-col gap-2"
      >
        <div className={"flex-row gap-2"} style={{ alignItems: "center" }}>
          <Controller
            name="permission"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth={true}
                options={options}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : (option?.label ?? "")
                }
                isOptionEqualToValue={(option, value) =>
                  option.grant === value.grant
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={"small"}
                    fullWidth={true}
                    label="Cerca autorizzazione"
                    error={!!errors.permission}
                    helperText={errors.permission?.grant?.message}
                  />
                )}
              />
            )}
          />
          {isSubmitting ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={"18px"} />
            </div>
          ) : (
            <Tooltip title={"Aggiungi"}>
              <AddCircleOutlineIcon
                style={{ color: "rebeccapurple" }}
                onClick={onSubmit}
              />
            </Tooltip>
          )}
        </div>
      </form>
      <div
        className="flex-col"
        style={{
          paddingBottom: ".25em",
          borderBottom: "1px solid #ededed",
          marginBottom: "1em",
        }}
      >
        <h3 style={{ margin: 0, paddingBottom: ".25em" }}>
          {" "}
          Lista autorizzazioni
        </h3>
        <p
          style={{
            margin: 0,
            paddingBottom: 0,
            fontWeight: 300,
            fontSize: "12px",
            color: "grey",
          }}
        >
          La propagazione e la visibilità delle autorizzazioni può impiegare
          fino a 1 min prima di essere visibile qui.
        </p>
      </div>
      <Stack direction="column" spacing={1} maxWidth={"fit-content"}>
        {(customClaims?.grants ?? []).map((item: string) => (
          <Chip
            key={item}
            label={item}
            variant="outlined"
            onDelete={() => handleDelete(item)}
          />
        ))}
      </Stack>
    </div>
  );
}
